// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Pagination,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  useTheme,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ReportsTable from '../components/ReportsTable';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { getReports, getMachines } from '../services/supabaseService';
import { supabase } from '../supabase';
import OverallUptime from '../components/OverallUptime';
import useLocationUptime from '../hooks/useLocationUptime';
import useQuarterTimeLeft from '../hooks/useQuarterTimeLeft';
import { useMissingQuarterlyReports } from '../hooks/useMissingQuarterlyReports';
import { Link } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CloseIcon from '@mui/icons-material/Close';

// Styled components for enhanced visuals
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: 40,
    marginRight: theme.spacing(2),
    color: theme.palette.mode === 'dark' ? '#061ab1' : theme.palette.primary.main,
  },
}));

const SectionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

const Dashboard: React.FC = () => {
  const [reports, setReports] = useState([]);
  const { best, worst } = useLocationUptime();
  const { quarterData, quarterInfo } = useQuarterTimeLeft();
  const locationsWithoutReports = useMissingQuarterlyReports();
  const itemsPerPage = 5;
  const [page, setPage] = useState(1);
  const theme = useTheme();
  const [selectedReport, setSelectedReport] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);



  // Process worst locations:
  // 1. Filter out locations with 100% uptime if there are any with less than 100%
  // 2. Sort by percentage (ascending - lowest first)
  const processedWorstLocations = React.useMemo(() => {
    if (!worst || worst.length === 0) return [];

    // Check if there are any locations with less than 100% uptime
    const nonPerfectLocations = worst.filter(loc => loc.percentage < 100);

    // If there are locations with less than 100%, use only those
    const locationsToUse = nonPerfectLocations.length > 0 ? nonPerfectLocations : worst;

    // Sort by percentage (ascending - lowest first)
    return [...locationsToUse].sort((a, b) => a.percentage - b.percentage);
  }, [worst]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const paginatedLocationsWithoutReports = locationsWithoutReports.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalLocations = locationsWithoutReports.length;
  const totalPages = Math.ceil(totalLocations / itemsPerPage);

  useEffect(() => {
    const fetchReports = async () => {
      try {


        // Fetch reports directly from Supabase
        const { data: reportsData, error } = await supabase
          .from('reports')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10); // Limit to 10 most recent reports for the dashboard

        if (error) {
          console.error('Supabase fetch error:', error);
          throw error;
        }



        // Also fetch all machines once to have them available for lookup
        const allMachines = await getMachines();

        // Process the reports data to include the report_data contents
        let processedReports = reportsData.map(report => {
          // Get the report data
          const reportData = {
            id: report.id,
            ...report.report_data
          };

          // If we have a machineId but no machineName, try to find the machine name
          if (reportData.machineId && !reportData.machineName) {
            // Find the machine in the list of all machines
            const machine = allMachines.find(m => m.id === reportData.machineId);
            if (machine) {
              reportData.machineName = machine.name;
            } else {
              // If we can't find the machine, use the ID as the name
              reportData.machineName = reportData.machineId;
            }
          }

          return reportData;
        });

        // Sort by date (newest first)
        processedReports.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Take only the first 5 for the dashboard
        processedReports = processedReports.slice(0, 5);


        setReports(processedReports);
      } catch (error) {
        console.error("Failed to fetch reports for dashboard:", error);
      }
    };

    fetchReports();
  }, []);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Add this near the beginning of the component, after theme declaration
  const tableHeadStyle = theme.palette.mode === 'dark' ? {
    backgroundColor: '#424242', // Gray background in dark mode
    '& th': {
      color: '#fff', // White text in dark mode
      fontWeight: 'bold'
    }
  } : {};

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Overview of system performance, reports, and scheduled inspections.
        </Typography>

        {/* Overview Section */}
        <SectionHeader elevation={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DashboardIcon sx={{ mr: 2, fontSize: 28 }} />
            <Typography variant="h5">System Overview</Typography>
          </Box>
        </SectionHeader>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StyledCard elevation={3}>
              <CardContent sx={{ flexGrow: 1 }}>
                <IconWrapper>
                  <AssessmentIcon />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="medium">
                      Overall Uptime
                    </Typography>
                  </Box>
                </IconWrapper>
                <OverallUptime />
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/status"
                  size="medium"
                >
                  View Details
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard elevation={3}>
              <CardContent sx={{ flexGrow: 1 }}>
                <IconWrapper>
                  <TrendingUpIcon />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="medium">
                      Top Performing Locations
                    </Typography>
                  </Box>
                </IconWrapper>
                <Box sx={{ mt: 2 }}>
                  {best.map((location, index) => (
                    <React.Fragment key={location.location}>
                      {index > 0 && <Divider sx={{ my: 1 }} />}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body2">{location.location}</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {location.percentage.toFixed(2)}%
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/status"
                  size="medium"
                >
                  View All
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard elevation={3}>
              <CardContent sx={{ flexGrow: 1 }}>
                <IconWrapper>
                  <TrendingDownIcon />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="medium">
                      Bottom Performing Locations
                    </Typography>
                  </Box>
                </IconWrapper>
                <Box sx={{ mt: 2 }}>
                  {processedWorstLocations.map((location, index) => (
                    <React.Fragment key={location.location}>
                      {index > 0 && <Divider sx={{ my: 1 }} />}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body2">{location.location}</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          {location.percentage.toFixed(2)}%
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/status"
                  size="medium"
                >
                  View All
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Reports Section */}
        <SectionHeader elevation={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ListAltIcon sx={{ mr: 2, fontSize: 28 }} />
            <Typography variant="h5">Recent Reports</Typography>
          </Box>
        </SectionHeader>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledCard elevation={3}>
              <CardContent sx={{ flexGrow: 1 }}>
                <ReportsTable rows={reports} onViewReport={handleViewReport} />
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/reports"
                  size="medium"
                >
                  View All Reports
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Schedule Section */}
        <SectionHeader elevation={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 2, fontSize: 28 }} />
            <Typography variant="h5">Schedule & Timeline</Typography>
          </Box>
        </SectionHeader>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StyledCard elevation={3}>
              <CardContent sx={{ flexGrow: 1 }}>
                <IconWrapper>
                  <ScheduleIcon />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="medium">
                      Quarter Timeline
                    </Typography>
                  </Box>
                </IconWrapper>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body1" fontWeight="medium">{quarterInfo}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <PieChart width={250} height={250}>
                    <Pie
                      data={quarterData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#ffa726"
                      dataKey="value"
                      nameKey="name"
                    >
                      {quarterData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? "#ffa726" : "#66bb6a"} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </Box>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/schedule"
                  size="medium"
                >
                  View Schedule
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={8}>
            <StyledCard elevation={3}>
              <CardContent sx={{ flexGrow: 1 }}>
                <IconWrapper>
                  <ListAltIcon sx={{ color: theme.palette.mode === 'dark' ? '#061ab1' : 'inherit' }} />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="medium">
                      Quarterly Inspections Left
                    </Typography>
                  </Box>
                </IconWrapper>
                <TableContainer>
                  <Table aria-label="Quarterly Inspections Left">
                    <TableHead sx={tableHeadStyle}>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell>Inspection Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedLocationsWithoutReports.map((location) => (
                        <TableRow key={location} hover>
                          <TableCell component="th" scope="row">
                            {location}
                          </TableCell>
                          <TableCell>
                            <Typography color="warning.main" fontWeight="medium">Pending</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/schedule"
                  size="medium"
                >
                  View Schedule
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>
        </Grid>
      </Box>

      {/* Report Detail Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Report Details
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Location</Typography>
                <Typography variant="body1">{selectedReport.location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Machine</Typography>
                <Typography variant="body1">{selectedReport.machineName || selectedReport.machineId || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Start Date</Typography>
                <Typography variant="body1">{selectedReport.date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">End Date</Typography>
                <Typography variant="body1">{selectedReport.endDate || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Assignee</Typography>
                <Typography variant="body1">{selectedReport.assignee || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Cause</Typography>
                <Typography variant="body1">{selectedReport.cause || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Description</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Typography variant="body1">
                    {selectedReport.description || 'No description provided.'}
                  </Typography>
                </Paper>
              </Grid>
              {selectedReport.poNumber && (
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">PO Number</Typography>
                  <Typography variant="body1">{selectedReport.poNumber}</Typography>
                </Grid>
              )}
              {selectedReport.serviceNumber && (
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Service Number</Typography>
                  <Typography variant="body1">{selectedReport.serviceNumber}</Typography>
                </Grid>
              )}
              {selectedReport.ticketNumber && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">Ticket Number</Typography>
                  <Typography variant="body1">
                    <Link href={selectedReport.ticketNumber} target="_blank" rel="noopener">
                      {selectedReport.ticketNumber}
                    </Link>
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;